import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { fetchContent } from "../../api/content";
import {
  completePitch,
  confirmAgreement,
  fetchBidScreen,
  fetchJobBoard,
  fetchPitch,
  saveAgreement,
  submitBid,
  toggleShortlist,
  type Agreement,
  type Bid,
  type BidScreen as BidScreenData,
  type JobFilter,
  type JobTile,
  type PitchView,
} from "../../api/pitch";
import { Banner } from "../../components/Banner";
import { ScreenState } from "../../components/ScreenState";
import { attempt, messageOf } from "../../lib/errors";
import { registerDraftSender, saveOrQueue } from "../../lib/offlineSync";
import { useSession } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { useDebouncedCallback } from "../../lib/useDebounced";
import { AgreementScreen } from "../../screens/pitch/AgreementScreen";
import { BidScreen } from "../../screens/pitch/BidScreen";
import { JobBoardScreen } from "../../screens/pitch/JobBoardScreen";
import { DoneScreen } from "./DoneScreen";

registerDraftSender<Agreement>("agreement", saveAgreement);

const PAGE = 12;
const DEFAULT_FILTER: JobFilter = {
  search: "",
  budget: "any",
  posted: "any",
  urgentOnly: false,
  sort: "newest",
  show: "all",
  limit: PAGE,
  pageNumber: 0,
};

export function JobBoardRoute() {
  const { user } = useSession();
  const pitch = useAsync(() => Promise.all([fetchPitch(), fetchContent()]), []);
  const [view, setView] = useState<PitchView | null>(null);
  const [filter, setFilter] = useState<JobFilter>(DEFAULT_FILTER);
  const [tiles, setTiles] = useState<JobTile[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (pitch.data) setView(pitch.data[0]);
  }, [pitch.data]);

  const load = useCallback(async (next: JobFilter, append: boolean) => {
    setLoadingJobs(true);
    try {
      const page = await fetchJobBoard(next);
      setTiles((current) => (append ? [...current, ...page.items] : page.items));
      setTotal(page.total);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const loadSoon = useDebouncedCallback((next: JobFilter) => void load(next, false), 300);

  useEffect(() => {
    void load(DEFAULT_FILTER, false);
  }, [load]);

  if (finished && view) {
    return (
      <DoneScreen
        kicker="Step 3 complete"
        title="You can read a brief, pitch to win, and protect yourself with an agreement."
        sub={`You have ${view.step.bidsCounted ?? view.bidsCounted} counted bids at an average ${view.step.readiness ?? view.readiness}% application-readiness. Next: get paid-ready and confirmed Work-Enabled.`}
        next={{ to: "/gig/4", label: "Start Step 4" }}
      />
    );
  }

  return (
    <ScreenState loading={pitch.loading} error={pitch.error} onRetry={pitch.reload}>
      {view && pitch.data && user?.track ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <JobBoardScreen
            trackLabel={pitch.data[1].tracks[user.track].label}
            pitch={view}
            filter={filter}
            tiles={tiles}
            total={total}
            loadingJobs={loadingJobs}
            busy={busy}
            onFilter={(patch) => {
              const next = { ...filter, ...patch, pageNumber: 0 };
              setFilter(next);
              if ("search" in patch) loadSoon(next);
              else void load(next, false);
            }}
            onLoadMore={() => {
              const next = { ...filter, pageNumber: filter.pageNumber + 1 };
              setFilter(next);
              void load(next, true);
            }}
            onToggleShortlist={(code) =>
              void attempt(
                () => undefined,
                setError,
                async () => {
                  const shortlist = await toggleShortlist(code);
                  setTiles((current) =>
                    current.map((tile) =>
                      tile.job.code === code
                        ? { ...tile, shortlisted: shortlist.includes(code) }
                        : tile,
                    ),
                  );
                  setView(await fetchPitch());
                },
              )
            }
            onComplete={() =>
              void attempt(setBusy, setError, async () => {
                const wasDone = view.done;
                setView(await completePitch());
                if (!wasDone) setFinished(true);
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}

export function BidRoute() {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const loaded = useAsync(() => Promise.all([fetchBidScreen(code), fetchPitch()]), [code]);
  const [screen, setScreen] = useState<BidScreenData | null>(null);
  const [result, setResult] = useState<Bid | null>(null);
  const [counted, setCounted] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) {
      setScreen(loaded.data[0]);
      setCounted(loaded.data[1].bidsCounted);
      setResult(null);
    }
  }, [loaded.data]);

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {screen ? (
        <BidScreen
          key={screen.job.code}
          screen={screen}
          result={result}
          bidsCounted={counted}
          busy={busy}
          error={error}
          onSubmit={(price, text) =>
            void attempt(setBusy, setError, async () => {
              const bid = await submitBid(screen.job.code, price, text);
              setResult(bid);
              setCounted((await fetchPitch()).bidsCounted);
            })
          }
          onToggleShortlist={() =>
            void attempt(
              () => undefined,
              setError,
              async () => {
                const shortlist = await toggleShortlist(screen.job.code);
                setScreen({ ...screen, shortlisted: shortlist.includes(screen.job.code) });
              },
            )
          }
          onNext={() => void navigate("/gig/3")}
        />
      ) : null}
    </ScreenState>
  );
}

export function AgreementRoute() {
  const loaded = useAsync(fetchPitch, []);
  const [view, setView] = useState<PitchView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) setView(loaded.data);
  }, [loaded.data]);

  const saveSoon = useDebouncedCallback((agreement: Agreement) => {
    setMessage(null);
    void saveOrQueue("agreement", "agreement", agreement, async (value) =>
      setView(await saveAgreement(value)),
    ).catch((e: unknown) => setError(messageOf(e)));
  }, 400);

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {view ? (
        <>
          {error ? <Banner tone="bad">{error}</Banner> : null}
          <AgreementScreen
            pitch={view}
            busy={busy}
            message={message}
            onChange={saveSoon}
            onSave={() =>
              void attempt(setBusy, setError, async () => {
                setView(await confirmAgreement());
                setMessage("✓ Agreement saved. Reuse it for every new client.");
              })
            }
          />
        </>
      ) : null}
    </ScreenState>
  );
}
